<template>
  <div>
    <div v-if="loading" class="loading-overlay">
      <div class="spinner-border text-light" role="status">
        <span class="visually-hidden">Loading...</span>
      </div>
    </div>

    <div v-else-if="error" class="error-container section-normal bg-color-primary">
      <div class="container">
        <div class="alert alert-danger">{{ error }}</div>
        <router-link to="/inventory" class="btn btn--normal btn--primary">
          Return to Inventory
        </router-link>
      </div>
    </div>

    <div v-else>
      <div class="car-detail-hero" v-if="car">
        <div class="car-detail-hero-top">
          <div class="d-flex align-items-center gap-2">
            <Icon icon="garden:check-badge-fill-16" class="iconify text-color-quinary" />
            <span class="paragraph--normal text-color-senary">This car is</span>
            <span class="paragraph--normal text-color-quinary">{{ car.status }}</span>
          </div>
          <div>
            <a href="tel:+421949006016" class="btn btn--primary btn--mini">CALL US</a>
          </div>
        </div>
        <div class="car-detail-hero-bottom">
          <div>
            <span class="badge badge--secondary badge--big d-md-none mb-3">{{ car.status }}</span>
            <div class="paragraph--xlarge wide text-uppercase text-color-quinary mb-2 notranslate" translate="no">
              {{ car.year }}
            </div>
            <div class="heading--0 lh-1 text-uppercase text-color-quinary mb-3 notranslate" translate="no">
              {{ car.brand }}<br />
              {{ car.model }}
            </div>
            <div v-if="car.trimLine" class="paragraph--xlarge wide text-uppercase text-color-quinary notranslate" translate="no">
              {{ car.trimLine }}
            </div>
          </div>
          <div class="text-end d-md-block d-none">
            <span class="badge badge--secondary badge--big">{{ car.status }}</span>
            <div class="heading--1 lh-1 text-uppercase text-color-quinary mt-4 mb-3 notranslate" translate="no">
              {{ formatPrice(car.nettoPrice) }}€
            </div>
            <div class="paragraph--xlarge wide text-uppercase text-color-senary">
              NETTO PRICE
            </div>
            <button
              v-if="car.status !== 'Sold' && car.status !== 'sold'"
              class="detail-b2b mt-3"
              type="button"
              @click="openB2B"
            >
              Get B2B prices
            </button>
          </div>
        </div>

      <div class="car-gallery">
        <!-- Main Swiper -->
        <div class="custom-pagination-detail">
          <span
            class="paragraph--xlarge text-color-quinary wide arrow-next"
            @click="goNext"
            >NEXT</span
          >
          <div class="custom-pagination"></div>
          <span
            class="paragraph--xlarge text-color-quinary wide arrow-prev"
            @click="goPrev"
            >PREV</span
          >
        </div>

        <swiper
          :modules="modules"
          :slides-per-view="1"
          :space-between="10"
          :pagination="{
            el: '.custom-pagination',
            type: 'progressbar',
            clickable: true,
            progressbarOpposite: false,
          }"
          :autoplay="{
            delay: 3000,
            disableOnInteraction: false,
          }"
          class="swiper-detail"
          @swiper="onMainSwiperInit"
        >
          <swiper-slide v-for="(image, index) in carImages" :key="index" @click="openFromGrid(index)" style="cursor: pointer;">
            <img
              class="car-detail-hero--img"
              :src="image"
              :alt="car.brand + ' ' + car.model"
            />
          </swiper-slide>
        </swiper>

      </div>
    </div>

    <!-- Mobile price section below hero -->
    <div class="section-mini bg-color-tertiary d-md-none" v-if="car">
      <div class="container">
        <div class="d-flex justify-content-around car-item--bottom">
          <div class="text-center">
            <div class="paragraph--xxlarge text-uppercase text-color-quinary">
              {{ formatPrice(car.nettoPrice) }}€
            </div>
            <div class="paragraph--small wide text-uppercase text-color-senary lh-1">
              NETTO
            </div>
          </div>
          <div class="text-center">
            <div class="paragraph--xxlarge text-uppercase text-color-quinary">
              {{ formatPrice(car.grossPrice) }}€
            </div>
            <div class="paragraph--small wide text-uppercase text-color-senary lh-1">
              GROSS
            </div>
          </div>
        </div>
        <button
          v-if="car.status !== 'Sold' && car.status !== 'sold'"
          class="detail-b2b detail-b2b--mobile"
          type="button"
          @click="openB2B"
        >
          Get B2B prices
        </button>
      </div>
    </div>

    <!-- Thumbnails grid below hero -->
    <div class="section-mini bg-color-primary" v-if="carImages.length > 1">
      <div class="container">
        <div class="d-flex align-items-center justify-content-between border-bottom-quinary padding-bottom-3-lg padding-bottom-2 margin-bottom-4-lg margin-bottom-3">
          <h2 class="heading--4 fw-light text-color-quinary text-uppercase mb-0">
            GALLERY
          </h2>
          <a href="#" @click.prevent="showGallery(0)" class="btn btn--primary btn--mini d-none d-md-inline-flex">
            <Icon icon="mdi:image-multiple" class="iconify me-1" /> VIEW ALL {{ carImages.length }} PHOTOS
          </a>
        </div>
        <div class="thumbnails-grid">
          <div
            v-for="(image, index) in gridThumbnails"
            :key="'thumb-' + index"
            class="thumbnail-item"
            :class="{ 'thumbnail-last': index === gridThumbnails.length - 1 }"
            @click="showGallery(0)"
          >
            <img :src="image" :alt="car.brand + ' ' + car.model + ' photo ' + (index + 1)" />
            <div v-if="index === gridThumbnails.length - 1" class="thumbnail-overlay">
              <Icon icon="mdi:image-multiple" class="iconify" />
              <span>VIEW FULL GALLERY</span>
            </div>
          </div>
        </div>
        <div class="mt-4 d-md-none">
          <a href="#" @click.prevent="showGallery(0)" class="btn btn--normal btn--secondary w-100 justify-content-center">
            <Icon icon="mdi:image-multiple" class="iconify me-2" /> VIEW ALL {{ carImages.length }} PHOTOS
          </a>
        </div>
      </div>
    </div>


    <div class="section-small bg-color-primary" v-if="car">

      <div class="container">
        <h2
          class="heading--4 fw-light text-color-quinary text-uppercase border-bottom-quinary padding-bottom-3-lg padding-bottom-2 margin-bottom-4-lg margin-bottom-3"
        >
          VEHICLE DETAILS
        </h2>

        <div class="row margin-bottom-10-lg margin-bottom-5 car-details notranslate" translate="no">
          <div class="col-lg-4 col-6" v-if="car.brand">
            <div
              class="paragraph--xxlarge d-flex flex-md-row flex-column gap-md-2 mb-md-2 mb-4"
            >
              <span class="text-color-senary order-md-1 order-2">Brand</span
              ><span class="text-color-quinary order-md-2 order-1">{{ car.brand }}</span>
            </div>
          </div>

          <div class="col-lg-4 col-6" v-if="car.model">
            <div
              class="paragraph--xxlarge d-flex flex-md-row flex-column gap-md-2 mb-md-2 mb-4"
            >
              <span class="text-color-senary order-md-1 order-2">Model</span
              ><span class="text-color-quinary order-md-2 order-1">{{ car.model }}</span>
            </div>
          </div>

          <div class="col-lg-4 col-6" v-if="car.year">
            <div
              class="paragraph--xxlarge d-flex flex-md-row flex-column gap-md-2 mb-md-2 mb-4"
            >
              <span class="text-color-senary order-md-1 order-2">Year</span
              ><span class="text-color-quinary order-md-2 order-1">{{ car.year }}</span>
            </div>
          </div>

          <div class="col-lg-4 col-6" v-if="car.power">
            <div
              class="paragraph--xxlarge d-flex flex-md-row flex-column gap-md-2 mb-md-2 mb-4"
            >
              <span class="text-color-senary order-md-1 order-2">Engine Power</span
              ><span class="text-color-quinary order-md-2 order-1">{{ car.power }} HP</span>
            </div>
          </div>
          <div class="col-lg-4 col-6" v-if="car.transmission">
            <div
              class="paragraph--xxlarge d-flex flex-md-row flex-column gap-md-2 mb-md-2 mb-4"
            >
              <span class="text-color-senary order-md-1 order-2">Transmission</span
              ><span class="text-color-quinary order-md-2 order-1">{{ capitalizeTransmission(car.transmission) }}</span>
            </div>
          </div>

          <div class="col-lg-4 col-6" v-if="car.drive">
            <div
              class="paragraph--xxlarge d-flex flex-md-row flex-column gap-md-2 mb-md-2 mb-4"
            >
              <span class="text-color-senary order-md-1 order-2">Drive</span
              ><span class="text-color-quinary order-md-2 order-1">{{ expandDriveType(car.drive) }}</span>
            </div>
          </div><div class="col-lg-4 col-6" v-if="car.category">
            <div
              class="paragraph--xxlarge d-flex flex-md-row flex-column gap-md-2 mb-md-2 mb-4"
            >
              <span class="text-color-senary order-md-1 order-2">Car Body</span
              ><span class="text-color-quinary order-md-2 order-1">{{ car.category }}</span>
            </div>
          </div>

          <div class="col-lg-4 col-6" v-if="car.mileage">
            <div
              class="paragraph--xxlarge d-flex flex-md-row flex-column gap-md-2 mb-md-2 mb-4"
            >
              <span class="text-color-senary order-md-1 order-2">Mileage</span
              ><span class="text-color-quinary order-md-2 order-1">{{ car.mileage }} km</span>
            </div>
          </div>
          <div class="col-lg-4 col-6" v-if="car.exteriorColor">
            <div
              class="paragraph--xxlarge d-flex flex-md-row flex-column gap-md-2 mb-md-2 mb-4"
            >
              <span class="text-color-senary order-md-1 order-2">Exterior Color</span
              ><span class="text-color-quinary order-md-2 order-1">{{ car.exteriorColor }}</span>
            </div>
          </div>
          <div class="col-lg-4 col-6" v-if="car.interiorColor">
            <div
              class="paragraph--xxlarge d-flex flex-md-row flex-column gap-md-2 mb-md-2 mb-4"
            >
              <span class="text-color-senary order-md-1 order-2">Interior Color</span
              ><span class="text-color-quinary order-md-2 order-1">{{ car.interiorColor }}</span>
            </div>
          </div>

        </div>

        <h2
          class="heading--4 fw-light text-color-quinary text-uppercase border-bottom-quinary padding-bottom-3-lg padding-bottom-2 margin-bottom-4-lg margin-bottom-3"
        >
          VEHICLE SPECIFICATION
        </h2>

        <div class="row align-items-center" v-if="car.specifications && formattedSpecifications.length > 0 ">
          <!-- First column of specifications (up to 17 items) -->
          <div class="col-lg-6 mb-lg-0 mb-5">
            <ul class="text-color-senary mb-0 text-list">
              <li v-for="(spec, index) in firstColumnSpecs" :key="index">{{ spec }}</li>
            </ul>
          </div>
          <!-- Second column of specifications (items after the first 17) -->
          <div class="col-lg-6" v-if="secondColumnSpecs.length > 0">
            <ul class="text-color-senary mb-0 text-list">
              <li v-for="(spec, index) in secondColumnSpecs" :key="index">{{ spec }}</li>
            </ul>
          </div>
        </div>

        <!-- Fallback message if no specifications are provided -->
        <div class="row align-items-center" v-else>
          <div class="col-12">
            <p class="text-color-senary">No specifications available for this vehicle.</p>
          </div>
          </div>

        <!-- Mobile.de link section -->
        <div v-if="car.mobileDeUrl" class="mobile-de-section margin-top-5">
          <div class="d-flex flex-md-row flex-column align-items-md-center justify-content-between gap-3 border-top-quinary padding-top-3">
            <div>
              <div class="paragraph--xlarge text-color-quinary mb-1">View full listing on mobile.de</div>
              <div class="paragraph--normal text-color-senary">See original dealer listing with all details, financing options and more</div>
            </div>
            <a :href="car.mobileDeUrl" target="_blank" rel="noopener noreferrer" class="btn btn--primary btn--normal mobile-de-btn">
              <svg class="mobile-de-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" width="18" height="18">
                <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6M15 3h6v6M10 14L21 3" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
              VIEW ON MOBILE.DE
            </a>
          </div>
        </div>

          </div>
        <!-- Price section temporarily hidden
        <div class="car-price-card bg-color-tertiary p-4 rounded mb-4">
          <h3 class="heading--4 text-color-quinary mb-4">Price</h3>
          <div class="car-price-card--price-netto">
            {{ formatPrice(car.nettoPrice) }}€
            <span>netto</span>
          </div>
          <div class="car-price-card--price-brutto mb-4">
            {{ formatPrice(car.grossPrice) }}€
            <span>brutto</span>
          </div>
          <div v-if="car.status !== 'Sold'">
            <a href="tel:+421123456789" class="btn btn--normal btn--primary w-100 mb-3">
              <Icon icon="uil:phone" class="iconify" />
              CALL FOR DETAILS
            </a>
            <router-link to="/contacts" class="btn btn--normal btn--secondary w-100">
              <Icon icon="uil:envelope" class="iconify" />
              INQUIRE ABOUT THIS CAR
            </router-link>
          </div>
          <div v-else class="sold-notice">
            <div class="alert alert-danger text-center">
              THIS VEHICLE HAS BEEN SOLD
            </div>
            <router-link to="/inventory" class="btn btn--normal btn--primary w-100 mt-3">
              VIEW OTHER VEHICLES
            </router-link>
          </div>
        </div>

        <div class="share-card bg-color-tertiary p-4 rounded">
          <h3 class="heading--4 text-color-quinary mb-4">Share This Car</h3>
          <div class="share-buttons">
            <a href="#" class="share-button">
              <i class="uil uil-facebook"></i>
            </a>
            <a href="#" class="share-button">
              <i class="uil uil-twitter"></i>
            </a>
            <a href="#" class="share-button">
              <i class="uil uil-whatsapp"></i>
            </a>
            <a href="#" class="share-button">
              <i class="uil uil-envelope"></i>
            </a>
          </div>
        </div>
        -->
<!--
        <div class="row">
          <div class="col-12">
            <h3 class="heading--4 text-color-quinary text-uppercase mb-4 mt-4">Similar Vehicles</h3>
            <div class="row">
              <div class="col-lg-4 col-md-6 mb-4" v-for="similarCar in similarCars" :key="similarCar.id">
                <CarItem :car="similarCar" />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="container text-center mt-5 mb-5">
        <router-link to="/inventory" class="btn btn--normal btn--primary mt-3">
          View All Vehicles
        </router-link>
      </div>
    -->
      </div>
    <!-- Sales Contacts Section - Only shown when agents are available -->
    <div class="section-small bg-color-tertiary" v-if="car && car.agents && car.agents.length > 0">
      <div class="container">
        <h2
          class="heading--4 fw-light text-color-quinary text-uppercase border-bottom-quinary padding-bottom-3-lg padding-bottom-2 margin-bottom-4-lg margin-bottom-3"
        >
          SALES CONTACTS
        </h2>

        <div class="row align-items-center">
          <div class="col-lg-6 mb-lg-0 mb-5" v-for="agent in car.agents" :key="agent.id">
            <div class="contact-item">
              <div class="contact-item--img mb-3">
                <img v-if="agent.primary_image" :src="agent.primary_image.url" :alt="agent.name" />
                <img v-else src="/frontend/assets/images/person.png" :alt="agent.name" />
              </div>
              <div class="d-flex align-items-center justify-content-between">
                <div class="paragraph--xxxlarge text-color-quinary">
                  {{ agent.name }}
                </div>
                <div class="d-flex align-items-center gap-3" v-if="agent.languages">
                  <Icon v-if="agent.languages.includes('german')" name="flagpack:de" size="18" />
                  <Icon v-if="agent.languages.includes('english')" name="flagpack:gb-ukm" size="18" />
                </div>
              </div>
              <div
                class="d-flex align-items-center justify-content-between border-bottom margin-bottom-2 pb-4"
              >
                <div class="text-color-senary">{{ agent.position || 'Sales Executive' }}</div>
                <div class="text-color-senary">{{ agent.languages || 'English' }}</div>
              </div>
              <div class="row align-items-center">
                <div class="col-sm-6 mb-sm-0 mb-4" v-if="agent.phone">
                  <div class="d-flex align-items-center gap-3">
                    <div class="icon-circle">
                      <Icon icon="ic:baseline-local-phone" class="iconify phonis" />
                    </div>
                    <span class="paragraph--xlarge text-color-quinary">{{ agent.phone }}</span>
                  </div>
                </div>
                <div class="col-sm-6" v-if="agent.email">
                  <div class="d-flex align-items-center gap-3">
                    <div class="icon-circle">
                      <Icon icon="ic:sharp-email" class="iconify phonis" />
                    </div>
                    <span class="paragraph--xlarge text-color-quinary">{{ agent.email }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <!-- Gallery Section -->
    <div class="section-small bg-color-primary">
      <div class="container">
        <h2
          class="heading--4 fw-light text-color-quinary text-uppercase border-bottom-quinary padding-bottom-3-lg padding-bottom-2 margin-bottom-5-lg margin-bottom-3"
        >
          <span class="notranslate" translate="no">SELECTED</span> MOMENTS
        </h2>
        <MasonryGallery />
      </div>
    </div>

      <FeaturedCars :show-filter="false" :exclude-id="car ? car.id : null" />

      <app-map />
      <AppMarquee />
    </div>

    <!-- Gallery Grid Overlay -->
    <teleport to="body">
      <div class="gallery-grid-overlay" v-if="gridVisible" @click.self="closeGrid">
        <div class="gallery-grid-header">
          <span class="gallery-grid-count">{{ carImages.length }} PHOTOS</span>
          <button class="gallery-grid-close" @click="closeGrid">
            <Icon icon="ph:x-bold" class="iconify" />
          </button>
        </div>
        <div class="gallery-grid-scroll">
          <div class="gallery-grid">
            <div
              v-for="(image, index) in carImages"
              :key="'grid-' + index"
              class="gallery-grid-item"
              @click="openFromGrid(index)"
            >
              <img :src="image" :alt="(car ? car.brand + ' ' + car.model : '') + ' photo ' + (index + 1)" />
            </div>
          </div>
        </div>
      </div>
    </teleport>

    <!-- Fullscreen Lightbox -->
    <vue-easy-lightbox
      :visible="galleryVisible"
      :imgs="carImages"
      :index="galleryIndex"
      :moveDisabled="false"
      :enableZoom="true"
      :zoomScale="3"
      :loop="true"
      :teleport="'body'"
      :noClose="false"
      :showArrows="true"
      escDisabled
      :swipeTolerance="50"
      @hide="onLightboxClose"
    ></vue-easy-lightbox>

  </div>


</template>

<script>
import { b2bModal } from '../b2bModal.js';
import CarItem from '../components/CarItem.vue';
import AppMarquee from '../components/AppMarquee.vue';
import axios from 'axios';
import { Pagination, Autoplay } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/vue';
import AppMap from '../components/AppMap.vue';
import VueEasyLightbox from 'vue-easy-lightbox';
import MasonryGallery from '../components/MasonryGallery.vue';
import FeaturedCars from '../components/FeaturedCars.vue';

export default {
  components: {
    CarItem,
    AppMarquee,
    Swiper,
    SwiperSlide,
    AppMap,
    VueEasyLightbox,
    MasonryGallery,
    FeaturedCars
  },
  data() {
    return {
      car: null,
      similarCars: [],
      loading: true,
      error: null,
      currentImageIndex: 0,
      mainSwiper: null,
      gridVisible: false,
      galleryVisible: false,
      galleryIndex: 0,
      modules: [Pagination, Autoplay],
      carImages: []
    };
  },
  computed: {
    gridThumbnails() {
      return this.carImages.slice(0, 8);
    },
    currentImage() {
      return this.carImages[this.currentImageIndex];
    },
    formattedSpecifications() {
      if (!this.car || !this.car.specifications) {
        return [];
      }
      // Split the specifications by new line and filter out empty lines and null values
      return this.car.specifications.split('\n')
        .filter(spec => spec && spec.trim() !== '' && spec.trim().toLowerCase() !== 'null');
    },
    firstColumnSpecs() {
      // Calculate the midpoint to evenly distribute specifications between columns
      const totalSpecs = this.formattedSpecifications.length;
      const midpoint = Math.ceil(totalSpecs / 2);
      return this.formattedSpecifications.slice(0, midpoint);
    },
    secondColumnSpecs() {
      // Get specifications for the second column (second half)
      const totalSpecs = this.formattedSpecifications.length;
      const midpoint = Math.ceil(totalSpecs / 2);
      return this.formattedSpecifications.slice(midpoint);
    }
  },
  mounted() {
    this.fetchCarDetails();
  },
  methods: {
    openB2B() {
      b2bModal.show(this.car, 'detail');
    },

    formatPrice(price) {
      if (price === null || price === undefined || price === 0) {
        return 'Price on request';
      }

      // Convert string to number if needed
      const numericPrice = typeof price === 'string' ? parseFloat(price) : price;

      // Format the price with thousands separator
      return new Intl.NumberFormat('de-DE', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
        useGrouping: true
      }).format(numericPrice);
    },

    // Expand drive type abbreviations to full names
    expandDriveType(driveType) {
      if (!driveType) return 'Not specified';

      // Make the input uppercase for case-insensitive matching
      const upperDriveType = driveType.toUpperCase();

      // Map of common drive type abbreviations to full names
      const driveTypeMap = {
        'FWD': 'Front-Wheel Drive',
        'RWD': 'Rear-Wheel Drive',
        'AWD': 'All-Wheel Drive',
        '4WD': 'Four-Wheel Drive'
      };

      // Check if the uppercase version matches any of our keys
      for (const [abbr, fullName] of Object.entries(driveTypeMap)) {
        if (upperDriveType === abbr) {
          return fullName;
        }
      }

      // If no match found, return the original value
      return driveType;
    },
    // Capitalize the first letter of each word in the transmission type
    capitalizeTransmission(transmission) {
      if (!transmission) return 'Not specified';
      
      // Split the string by spaces and capitalize each word
      return transmission.split(' ')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(' ');
    },
    onMainSwiperInit(swiper) {
      this.mainSwiper = swiper;
    },
    showGallery(index) {
      // Open grid overlay instead of lightbox directly
      this.gridVisible = true;
      document.body.style.overflow = 'hidden';
      // Pause main swiper autoplay
      if (this.mainSwiper && this.mainSwiper.autoplay) {
        this.mainSwiper.autoplay.stop();
      }
    },

    closeGrid() {
      this.gridVisible = false;
      document.body.style.overflow = '';
      // Resume main swiper autoplay
      if (this.mainSwiper && this.mainSwiper.autoplay) {
        this.mainSwiper.autoplay.start();
      }
    },

    openFromGrid(index) {
      this.galleryIndex = index;
      this.galleryVisible = true;
      this.addCustomGalleryStyles();
    },

    // Add custom styles for the lightbox
    addCustomGalleryStyles() {
      // Check if styles are already added
      if (document.getElementById('custom-lightbox-styles')) return;

      const styleEl = document.createElement('style');
      styleEl.id = 'custom-lightbox-styles';
      styleEl.innerHTML = `
        .vel-modal {
          background-color: #000000 !important;
          z-index: 10001 !important;
        }
        .vel-img-wrapper, .vel-img {
          background-color: #000000 !important;
        }
        .vel-toolbar, .vel-actions {
          z-index: 10002 !important;
        }
        .vel-btns-next, .vel-btns-prev {
          background-color: rgba(0, 0, 0, 0.95) !important;
          border-radius: 50% !important;
          width: 60px !important;
          height: 60px !important;
          display: flex !important;
          align-items: center !important;
          justify-content: center !important;
          box-shadow: 0 0 20px rgba(0, 0, 0, 0.9) !important;
          z-index: 10000 !important;
          border: 3px solid rgba(255, 255, 255, 0.8) !important;
          position: fixed !important;
        }
        .vel-btns-next {
          right: calc(20% - 30px) !important;
        }
        .vel-btns-prev {
          left: calc(20% - 30px) !important;
        }
        .vel-icon {
          filter: drop-shadow(0 0 5px rgba(0, 0, 0, 1)) !important;
          width: 30px !important;
          height: 30px !important;
        }
      `;
      document.head.appendChild(styleEl);
    },

    // Handle lightbox close - go back to grid
    onLightboxClose() {
      this.galleryVisible = false;
    },
    goNext() {
      if (this.mainSwiper) {
        this.mainSwiper.slideNext();
      }
    },
    goPrev() {
      if (this.mainSwiper) {
        this.mainSwiper.slidePrev();
      }
    },
    async fetchCarDetails() {
      this.loading = true;
      this.error = null;

      // Get the car ID from route params - works with both URL formats
      const carId = this.$route.params.id;

      try {
        // Set up CSRF token for all axios requests
        axios.defaults.headers.common['X-CSRF-TOKEN'] = document.querySelector('meta[name="csrf-token"]').getAttribute('content');

        // Fetch car details from the API
        const response = await axios.get(`/api/vehicles/${carId}`);

        // Check for images in different possible locations in the API response
        let vehicleImages = [];
        if (response.data.images && Array.isArray(response.data.images)) {
          vehicleImages = response.data.images;
        } else if (response.data.vehicle_images && Array.isArray(response.data.vehicle_images)) {
          vehicleImages = response.data.vehicle_images;
        } else {
          // Try to make a separate API call to get the vehicle images
          try {
            const imagesResponse = await axios.get(`/api/vehicles/${carId}/images`);
            if (imagesResponse.data && Array.isArray(imagesResponse.data)) {
              vehicleImages = imagesResponse.data;
            }
          } catch (error) {
            console.error('Error fetching vehicle images:', error);
          }
        }

        console.log('Raw transmission value from API:', response.data.transmission);

        // Map the API response to our expected format
        this.car = {
          id: response.data.id,
          brand: response.data.make,
          model: response.data.model,
          year: response.data.year,
          nettoPrice: response.data.netto_price || response.data.price_netto || response.data.price,
          grossPrice: response.data.gross_price || response.data.price_brutto || Math.round((response.data.netto_price || response.data.price) * 1.2), // Fallback calculation if brutto not provided
          status: response.data.status || 'Available',
          // Additional fields that might be available from the API
          engine: response.data.engine || null,
          power: response.data.engine_power || null,
          transmission: response.data.transmission || null,
          drive: response.data.drive || null,
          mileage: response.data.mileage || null,
          category: response.data.category || null,
          exteriorColor: response.data.color || null,
          interiorColor: response.data.interior_color || null,
          description: response.data.description || 'A masterpiece of engineering and design.',
          // Vehicle specifications as bullet points
          specifications: response.data.specifications || '',
          // Mobile.de URL for external link
          trimLine: response.data.trim_line || null,
          mobileDeUrl: response.data.mobile_de_url || null,
          // Store the images we found
          images: vehicleImages,
          // Store the agents associated with this vehicle
          agents: response.data.agents || []
        };

        // Log agents data for debugging
        console.log('Vehicle agents:', response.data.agents);

        if (!this.car) {
          this.error = 'Vehicle not found';
          return;
        }

        // If we have images from the API, use them
        if (this.car.images && this.car.images.length > 0) {
          console.log('Processing vehicle images:', this.car.images);

          // Sort images by display_order if available
          const sortedImages = [...this.car.images].sort((a, b) => {
            // If both images have display_order, sort by that
            if (a.display_order !== undefined && b.display_order !== undefined) {
              return a.display_order - b.display_order;
            }
            // If only one has display_order, prioritize the one with display_order
            if (a.display_order !== undefined) return -1;
            if (b.display_order !== undefined) return 1;
            // If neither has display_order, maintain original order
            return 0;
          });

          console.log('Sorted images by display_order:', sortedImages);

          this.carImages = sortedImages.map(img => {
            // Handle different image object structures
            if (typeof img === 'string') {
              // If the image is already a string URL
              return img;
            }

            // Check if the image path already has the full URL
            if (img.path && (img.path.startsWith('http://') || img.path.startsWith('https://'))) {
              return img.path;
            } else if (img.path) {
              // If it's a relative path, prepend the storage URL
              return `/storage/${img.path}`;
            } else if (img.url) {
              return img.url;
            } else if (img.image_path) {
              // Some APIs might use image_path instead of path
              return `/storage/${img.image_path}`;
            } else if (img.image_url) {
              // Some APIs might use image_url instead of url
              return img.image_url;
            } else {
              // Fallback to default image if no path or URL is available
              return '/frontend/assets/images/car_detail.jpeg';
            }
          });

          console.log('Processed images:', this.carImages);
        } else {
          // Fallback to default images if no images are available
          this.carImages = [
            '/frontend/assets/images/car_detail.jpeg',
            '/frontend/assets/images/car_detail1.jpeg',
            '/frontend/assets/images/car_detail2.jpeg',
            '/frontend/assets/images/car_detail3.jpeg'
          ];
        }

        console.log('Car Images:', this.carImages);

        // Set page title & meta with full vehicle details
        const price = this.car.nettoPrice ? ` - ${new Intl.NumberFormat('de-DE').format(this.car.nettoPrice)}€` : '';
        const trim = this.car.trimLine ? ` ${this.car.trimLine}` : '';
        const seoTitle = `${this.car.year} ${this.car.brand} ${this.car.model}${trim}${price} | Selected Cars`;

        const mileage = this.car.mileage ? `${new Intl.NumberFormat('de-DE').format(this.car.mileage)} km, ` : '';
        const power = this.car.power ? `${this.car.power} HP, ` : '';
        const trans = this.car.transmission ? `${this.car.transmission}, ` : '';
        const color = this.car.exteriorColor ? `${this.car.exteriorColor}. ` : '';
        const priceText = this.car.nettoPrice ? `Price: ${new Intl.NumberFormat('de-DE').format(this.car.nettoPrice)}€ netto. ` : '';
        const seoDesc = `${this.car.year} ${this.car.brand} ${this.car.model}${trim}. ${mileage}${power}${trans}${color}${priceText}Sourced from Europe. Contact us for details and delivery.`;

        const { setPageSeo, setVehicleSeo } = await import('../seo.js');
        const primaryImage = this.carImages && this.carImages.length > 0 ? this.carImages[0] : null;
        const fullImageUrl = primaryImage && primaryImage.startsWith('http') ? primaryImage : (primaryImage ? 'https://selected.sk' + primaryImage : null);
        const vehicleSchema = setVehicleSeo(this.car, fullImageUrl);

        setPageSeo({
          title: seoTitle,
          description: seoDesc,
          image: fullImageUrl,
          keywords: `${this.car.brand} ${this.car.model} for sale, ${this.car.year} ${this.car.brand}, buy ${this.car.brand} ${this.car.model}, ${this.car.brand} ${this.car.model} europe, ${this.car.brand} dealer, ${this.car.brand} ${this.car.model} price`,
          breadcrumbs: [
            { name: 'Home', url: '/' },
            { name: 'Inventory', url: '/inventory' },
            { name: `${this.car.brand} ${this.car.model}`, url: `/car/${this.car.id}` }
          ],
          schema: vehicleSchema
        });

        // Get similar cars
        try {
          const similarResponse = await axios.get('/api/vehicles', {
            params: {
              limit: 3,
              exclude: carId
            }
          });

          // Check if response.data is an array or if it has a data property
          const vehiclesData = Array.isArray(similarResponse.data) ? similarResponse.data :
                            (similarResponse.data.data ? similarResponse.data.data : [similarResponse.data]);

          console.log('Similar vehicles API response:', vehiclesData);

          this.similarCars = vehiclesData.map(vehicle => ({
            id: vehicle.id,
            brand: vehicle.make,
            model: vehicle.model,
            year: vehicle.year,
            nettoPrice: vehicle.price_netto || vehicle.netto_price || vehicle.price,
            grossPrice: vehicle.price_brutto || vehicle.gross_price || Math.round(vehicle.price * 1.2),
            status: vehicle.status || 'Available',
            image: vehicle.primary_image ? vehicle.primary_image.url : null
          }));

          console.log('Mapped similar cars with images:', this.similarCars);
        } catch (error) {
          console.error('Error fetching similar cars:', error);
          // Fallback to empty array if similar cars can't be loaded
          this.similarCars = [];
        }

      } catch (err) {
        console.error('Error fetching car details:', err);
        this.error = 'Failed to load vehicle details. Please try again.';
      } finally {
        this.loading = false;
      }
    }
  },
  watch: {
    '$route.params.id': function() {
      this.fetchCarDetails();
    }
  }
};
</script>

<style scoped>
/* Add your scoped styles here */

/* Custom styling for lightbox arrows */

.thumbnail-container {
  position: relative;
  width: 100%;
  height: 100%;
  aspect-ratio: 1 / 1;
  cursor: pointer;
  transition: all 0.3s ease;
}
.thumbnail-container:hover:not(.gallery-overlay) {
  transform: translateY(-5px);
}

.thumbnail-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.gallery-overlay::after {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.85);
  z-index: 1;
}

.gallery-button {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 2;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: white;
  cursor: pointer;
  text-align: center;
  width: 100%;
}

.gallery-button .iconify {
  font-size: 28px;
  margin-bottom: 8px;
  filter: drop-shadow(0 0 3px rgba(255, 255, 255, 0.5));
}

.gallery-button span {
  font-size: 14px;
  font-weight: 700;
  letter-spacing: 1.5px;
  text-shadow: 0 0 4px rgba(0, 0, 0, 0.8);
}

.loading-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
}

.mobile-de-section {
  margin-top: 3rem;
}

.border-top-quinary {
  border-top: 1px solid rgba(255, 255, 255, 0.15);
  padding-top: 1.5rem;
}

.mobile-de-btn {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  white-space: nowrap;
  flex-shrink: 0;
}

.mobile-de-icon {
  flex-shrink: 0;
}

/* Gallery Grid Overlay */
.gallery-grid-overlay {
  position: fixed;
  inset: 0;
  z-index: 10000;
  background: #000;
  display: flex;
  flex-direction: column;
}

.gallery-grid-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem 1.5rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  flex-shrink: 0;
}

.gallery-grid-count {
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 0.15em;
  color: rgba(255, 255, 255, 0.6);
}

.gallery-grid-close {
  background: none;
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: #fff;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s;
}

.gallery-grid-close:hover {
  background: rgba(255, 255, 255, 0.1);
  border-color: rgba(255, 255, 255, 0.4);
}

.gallery-grid-close .iconify {
  font-size: 1.1rem;
}

.gallery-grid-scroll {
  flex: 1;
  overflow-y: auto;
  padding: 1rem;
  -webkit-overflow-scrolling: touch;
}

.gallery-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 6px;
  max-width: 1200px;
  margin: 0 auto;
}

.gallery-grid-item {
  aspect-ratio: 16 / 10;
  overflow: hidden;
  cursor: pointer;
  position: relative;
}

.gallery-grid-item::after {
  content: '';
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0);
  transition: background 0.2s;
}

.gallery-grid-item:hover::after {
  background: rgba(0, 0, 0, 0.2);
}

.gallery-grid-item img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
  transition: transform 0.3s ease;
}

.gallery-grid-item:hover img {
  transform: scale(1.03);
}

@media (max-width: 991px) {
  .gallery-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 767px) {
  .gallery-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 4px;
  }

  .gallery-grid-header {
    padding: 0.75rem 1rem;
  }

  .gallery-grid-scroll {
    padding: 0.5rem;
  }

  .mobile-de-btn {
    width: 100%;
    justify-content: center;
  }
}

.detail-b2b {
  display: inline-block;
  padding: 0.7rem 1.5rem;
  background: transparent;
  border: 1px solid rgba(255, 255, 255, 0.35);
  border-radius: 0;
  color: #ffffff;
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  cursor: pointer;
  transition: background-color 0.3s ease, color 0.3s ease, border-color 0.3s ease;
}

.detail-b2b:hover {
  background: #ffffff;
  border-color: #ffffff;
  color: #000000;
}

.detail-b2b--mobile {
  display: block;
  width: 100%;
  margin-top: 1.25rem;
}
</style>
